class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.addEventListener(
            "keyup",
            (evt) => evt.code === "Escape" && this.close()
        );
        this.querySelector("#CartDrawer-Overlay").addEventListener(
            "click",
            this.close.bind(this)
        );
        this.setHeaderCartIconAccessibility();
        
        // Listen for upsell click events inside this component
        this.addEventListener("click", this.handleUpsellClick.bind(this));

        // 🔄 GLOBAL EVENT LISTENER: Catches any cart changes and runs initialization again
        document.addEventListener("cart-drawer:updated", () => {
            console.log("🔄 Cart change caught: Re-initializing slider.");
            this.initUpsellSlider(true);
        });

        console.log("Updated....");
        this.initUpsellSlider();
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelectorAll(".cart-open-trigger");
        cartLink.forEach((links) => {
            links.setAttribute("role", "button");
            links.setAttribute("aria-haspopup", "dialog");
            links.addEventListener("click", (event) => {
                event.preventDefault();
                this.open(links);
            });
            links.addEventListener("keydown", (event) => {
                if (event.code.toUpperCase() === "SPACE") {
                    event.preventDefault();
                    this.open(links);
                }
            });
        });
    }

    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
            this.setSummaryAccessibility(cartDrawerNote);
        
        setTimeout(() => {
            this.classList.add("animate", "active");
            this.initUpsellSlider(true); // Force initialize on opening
        });

        this.addEventListener(
            "transitionend",
            () => {
                const containerToTrapFocusOn = this.classList.contains("is-empty")
                    ? this.querySelector(".drawer__inner-empty")
                    : document.getElementById("CartDrawer");
                const focusElement =
                    this.querySelector(".drawer__inner") ||
                    this.querySelector(".drawer__close");
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true }
        );

        document.body.classList.add("overflow-hidden");
    }

    close() {
        this.classList.remove("active");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");

        if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
            cartDrawerNote.setAttribute(
                "aria-controls",
                cartDrawerNote.nextElementSibling.id
            );
        }

        cartDrawerNote.addEventListener("click", (event) => {
            event.currentTarget.setAttribute(
                "aria-expanded",
                !event.currentTarget.closest("details").hasAttribute("open")
            );
        });

        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    }

    getRoute(subPath) {
        const root = window.Shopify?.routes?.root || '/';
        return `${root}${root.endsWith('/') ? '' : '/'}${subPath}`;
    }

    // ⚡ Intercept Upsell Clicks & Add to Cart via AJAX
    handleUpsellClick(event) {
        const button = event.target.closest(".cart-upsell__add-btn");
        if (!button) return; 

        // 🚀 CORE FIX: Stop the click from bubbling up to the <a> link wrapper tag
        event.preventDefault();
        event.stopPropagation();
        
        const variantId = button.getAttribute("data-variant-id");
        if (!variantId) return;

        const variantTitle = button.querySelector(".custom-product__variant-title");
        const loadingText = button.querySelector(".custom-product__loading-text");

        button.setAttribute("disabled", "disabled");
        if (variantTitle) variantTitle.style.display = "none";
        if (loadingText) loadingText.style.display = "inline-block";

        const formData = {
            items: [{
                id: parseInt(variantId),
                quantity: 1
            }],
            sections: ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble-mobile'],
            sections_url: window.location.pathname
        };

        fetch(this.getRoute('cart/add.js'), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest"
            },
            body: JSON.stringify(formData)
        })
        .then((response) => response.json())
        .then((parsedState) => {
            if (parsedState.status) {
                console.error("Shopify Cart Error:", parsedState.description);
                alert(parsedState.description);
                
                button.removeAttribute("disabled");
                if (variantTitle) variantTitle.style.display = "";
                if (loadingText) loadingText.style.display = "none";
                return;
            }

            console.log("🛒 Upsell Added Successfully! Updating content rendering views...");
            this.renderContents(parsedState);
        })
        .catch((error) => {
            console.error("AJAX Error adding upsell to cart:", error);
            button.removeAttribute("disabled");
            if (variantTitle) variantTitle.style.display = "";
            if (loadingText) loadingText.style.display = "none";
        });
    }

    // 🔄 Fires when an item is added via the Upsell button
    renderContents(parsedState) {
        this.querySelector(".drawer__inner").classList.contains("is-empty") &&
            this.querySelector(".drawer__inner").classList.remove("is-empty");
        
        this.productId = parsedState.id;
        
        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
            
            const sectionId = section.id;
            const sectionsSource = parsedState.sections || (parsedState.cart && parsedState.cart.sections);
            
            if (sectionsSource && sectionsSource[sectionId]) {
                sectionElement.innerHTML = this.getSectionInnerHTML(
                    sectionsSource[sectionId],
                    section.selector
                );
            }
        });

        setTimeout(() => {
            this.querySelector("#CartDrawer-Overlay").addEventListener(
                "click",
                this.close.bind(this)
            );
            
            // Re-initialize slider completely after changing via Upsell add
            this.initUpsellSlider(true); 
            this.open();
        }, 50);
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
        return [
            { id: "cart-drawer", selector: "#CartDrawer" },
            { id: "cart-icon-bubble" },
            { id: "cart-icon-bubble-mobile" }
        ];
    }

    setActiveElement(element) {
        this.activeElement = element;
    }

    // 🎡 The Engine that handles building and rebuilding the slider
    initUpsellSlider(forceRecreate = false) {
        const sliderElem = this.querySelector('#CartUpsellSplide');
        
        if (sliderElem && typeof Splide !== 'undefined') {
            // If forced, completely clear out Splide's marker class so we can mount clean HTML
            if (forceRecreate) {
                sliderElem.classList.remove('is-initialized');
            }

            if (sliderElem.classList.contains('is-initialized')) return;

            console.log("🚀 Initializing Splide Slider instance...");
            new Splide('#CartUpsellSplide', {
                type   : 'slide',
                perPage: 2,
                gap    : '2px',
                padding: { left: '0', right: '6rem' },
                pagination: false,
                arrows : false
            }).mount();
        }
    }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
    getSectionsToRender() {
        return [
            {
                id: "CartDrawer",
                section: "cart-drawer",
                selector: ".drawer__inner",
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
                selector: ".shopify-section",
            },
            {
                id: "cart-icon-bubble-mobile",
                section: "cart-icon-bubble-mobile",
                selector: ".shopify-section",
            },
        ];
    }

    // 🔄 Fires automatically when items are removed or quantity increments/decrements
    renderContents(parsedState) {
        // Run standard theme re-rendering rules to draw line item adjustments
        if (super.renderContents) {
            super.renderContents(parsedState);
        } else {
            this.getSectionsToRender().forEach((section) => {
                const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
                if (elementToReplace && parsedState.sections?.[section.section]) {
                    elementToReplace.innerHTML = new DOMParser()
                        .parseFromString(parsedState.sections[section.section], "text/html")
                        .querySelector(section.selector).innerHTML;
                }
            });
        }

        // 🚀 Every time a change completes, wait 50ms for HTML stabilization, then dispatch the update trigger to re-initialize Splide!
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent("cart-drawer:updated"));
        }, 50);
    }
}

customElements.define("cart-drawer-items", CartDrawerItems);